'use client';

import { Checkbox as DSCheckbox } from '@lulalife/design-system';
import get from 'lodash/get';
import type { ChangeEvent, ReactNode } from 'react';
import { useController } from 'react-hook-form';
import { SettingsFieldError } from '../SettingsFieldError';

interface CheckBoxProps {
  name: string;
  /** `ReactNode` because DS `Checkbox` takes one — a highlighted or composed label. */
  label?: ReactNode;
  /** Overrides the id/htmlFor pairing, which otherwise defaults to `name`. */
  id?: string;
  checked?: boolean;
  /** A real prop here: DS `Checkbox` writes the DOM property, so no attribute stringifying. */
  indeterminate?: boolean;
  disabled?: boolean;
  className?: string;
  hideFormError?: boolean;
  /** Writes `event.target.checked` rather than `event.target.value`, same as the legacy field. */
  returnBoolean?: boolean;
  handleClick?: ((event: ChangeEvent<HTMLInputElement>) => void) | null;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * Same `name`-bound, `useController`-wired API as
 * `src/components/Shared/FormFields/CheckBox.tsx` — only the rendered control
 * changed, from a reactstrap `FormGroup`/`Label`/`Input type="checkbox"` to the
 * design system's `Checkbox`. A call site swaps the import and keeps the props
 * it already passes.
 *
 * `returnBoolean` keeps its legacy default (`false`, i.e. write the value) so
 * that swap stays honest; the working-hours weekday fields pass it, as they did
 * before.
 *
 * Errors render through `SettingsFieldError` rather than the bare
 * `body-xs text-alert-error` span `FormFields/Toggle.tsx` uses — those are
 * unlayered legacy SCSS class names, and this kit already owns the DS-token
 * equivalent.
 *
 * Deliberately not carried over: `formGroupClass`, `inputSize`, `boxed`,
 * `labelClassName` and `labelProps`. All are reactstrap `FormGroup`/`Label`
 * concerns with no equivalent slot on DS `Checkbox`, which renders its own
 * `<label>` wrapper. (DS `Checkbox` does ship a `variant="boxed"`, but it draws
 * the design's bordered card, not the legacy `check-boxed` layout, so it is not
 * a like-for-like carry-over and no converted call site asks for it.)
 */
export const CheckBox = ({
  name,
  label,
  id,
  checked,
  indeterminate,
  disabled,
  className,
  hideFormError = false,
  returnBoolean = false,
  handleClick = null,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: CheckBoxProps) => {
  const {
    field,
    formState: { errors },
  } = useController({ name });

  const fieldError = get(errors, name);
  const fieldErrorMessage = typeof fieldError?.message === 'string' ? fieldError.message : undefined;

  const handleChange = (changeEvent: ChangeEvent<HTMLInputElement>) => {
    if (handleClick) {
      field.onChange(handleClick(changeEvent));
    } else if (returnBoolean) {
      field.onChange(changeEvent.target.checked);
    } else {
      field.onChange(changeEvent.target.value);
    }
  };

  return (
    <>
      <DSCheckbox
        id={id ?? name}
        label={label}
        checked={checked}
        indeterminate={indeterminate}
        disabled={disabled}
        className={className}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        onChange={handleChange}
        onBlur={field.onBlur}
      />
      {!hideFormError && fieldErrorMessage ? (
        <SettingsFieldError>{fieldErrorMessage}</SettingsFieldError>
      ) : null}
    </>
  );
};

export default CheckBox;
